import { TaskState } from '@modules/TasksModule/types';
import { InboxContext } from '../types';
import TasksModule from '@modules/TasksModule';
import ToastModule from '@modules/ToastModule';
import strings from '../constants/strings';
import { inboxState } from '../state';
import InboxModule from '..';
import { CALL_MISSED_TOAST_DURATION } from '../constants/missedCallToastDuration';
import { bubbleDetectLinkToMonolith } from '@lib/utils/tenantLinks';

// contains logic for managing external call tasks

export function setExternalCallToOffered(
  context: InboxContext,
  sessionId: string,
  sessionSid: string,
  userId: string,
  from: string,
  displayName: string,
  tenantId: string,
  queueName: string,
  queueId: string,
  queueEnterTime: string,
) {
  // only do for current user
  if (context.userId !== userId) {
    return;
  }
  context.logger.info('external call Offered', sessionSid);

  // check to see if we have a task here
  // it's possible that the session was created before the
  // user came online. if we don't find the task, create it here
  let task = TasksModule.getTask(sessionSid);
  if (!task) {
    // create a new task
    task = TasksModule.createTask({
      id: sessionSid,
      from: from,
      displayName: displayName,
      tenantId: tenantId,
      attributes: { sessionId, recordInteraction: true },
    });
  }

  ToastModule.showIncomingCallToast({
    id: task.id,
    from,
    displayName,
    tenantId,
    isInternal: false,
  });

  // update the task state
  TasksModule.setTaskState(task.id, TaskState.Offered);
  task.queueName = queueName;
  task.queueId = queueId;
  task.startedAt = new Date(queueEnterTime);
}

export function setExternalCallToAccepted(
  context: InboxContext,
  sessionSid: string,
  userId: string,
) {
  // only do for current user
  if (context.userId !== userId) {
    return;
  }
  context.logger.info('external call Accepted', sessionSid);

  const task = TasksModule.getTask(sessionSid);
  if (task) {
    ToastModule.hideIncomingCallToast(task.id);
    InboxModule.resetMissedCalls();
    TasksModule.setTaskState(task.id, TaskState.Accepted);

    // pop tab to accept green bubble
    bubbleDetectLinkToMonolith(task.tenantId, task.from);
  }
}

export function setExternalCallToRejected(
  context: InboxContext,
  taskId: string,
  userId: string,
) {
  // only do for current user
  if (context.userId !== userId) {
    return;
  }
  context.logger.info('external call Rejected', taskId);

  const task = TasksModule.getTask(taskId);
  if (task) {
    ToastModule.showToast({
      title: strings.labels.toasts.rejectExternalCallTitle,
      status: 'info',
    });

    TasksModule.setTaskAttributes(task.id, { recordInteraction: false });
    ToastModule.hideIncomingCallToast(task.id);
    TasksModule.setTaskState(task.id, TaskState.Rejected);
    TasksModule.deleteTask(task.id);
  }
}

export function setExternalCallToAbandoned(
  context: InboxContext,
  sessionSid: string,
) {
  context.logger.info('external call Abandoned', sessionSid);
  const task = TasksModule.getTask(sessionSid);
  if (task) {
    ToastModule.hideIncomingCallToast(task.id);
    ToastModule.showToast({
      title: strings.labels.toasts.callMissedExternalTitle,
      status: 'info',
      duration: CALL_MISSED_TOAST_DURATION,
    });
    TasksModule.setTaskAttributes(task.id, { recordInteraction: false });
    TasksModule.setTaskState(task.id, TaskState.Abandoned);
  }
}

export function setExternalCallToWrapup(
  context: InboxContext,
  sessionSid: string,
  userId: string,
  timestamp: string,
  wrapupAllowedMs: number,
) {
  // only do for current user
  if (context.userId !== userId) {
    return;
  }
  context.logger.info('external call WrapUp', sessionSid);

  const task = TasksModule.getTask(sessionSid);
  if (task) {
    TasksModule.setTaskState(task.id, TaskState.WrapUp);
    task.wrapUpEndsAt = new Date(timestamp).getTime() + wrapupAllowedMs;
  }
}

export function setExternalCallToCompleted(
  context: InboxContext,
  taskId: string,
) {
  context.logger.info('external call Completed', taskId);
  const task = TasksModule.getTask(taskId);
  if (task) {
    // handle classification
    // likely set a an inbox state var to show classification modal

    TasksModule.setTaskState(task.id, TaskState.Completed);
    TasksModule.deleteTask(task.id);
  }
}

export function setExternalCallToTimedOut(
  context: InboxContext,
  sessionSid: string,
  userId: string,
) {
  // only do for current user
  if (context.userId !== userId) {
    return;
  }
  context.logger.info('external call Timed Out', sessionSid);

  const task = TasksModule.getTask(sessionSid);
  if (task) {
    ToastModule.hideIncomingCallToast(task.id);
    InboxModule.handleMissedExternalCall();
    ToastModule.showToast({
      title: strings.labels.toasts.statusChangedTitle,
      status: 'info',
      duration: CALL_MISSED_TOAST_DURATION,
      toastBody: strings.labels.toasts.statusChangedBody(
        inboxState.missedCallsExternal,
      ),
    });
    TasksModule.setTaskAttributes(task.id, { recordInteraction: false });
    TasksModule.setTaskState(task.id, TaskState.TimedOut);
  }
}
